import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Subscription } from "@/types/subscription";

interface UseSubscriptionNavigationProps {
  loading: boolean;
  isSubscriptionLoading: boolean;
  user: any;
  subscriptionStatus: Subscription | undefined;
}

export const useSubscriptionNavigation = ({
  loading,
  isSubscriptionLoading,
  user,
  subscriptionStatus
}: UseSubscriptionNavigationProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const hasNavigatedRef = useRef(false);
  const initialCheckComplete = useRef(false);

  useEffect(() => {
    // Skip navigation checks if auth is still loading or subscription is loading
    if (loading || isSubscriptionLoading || !user) {
      return;
    }

    // Check if user just signed up - highest priority check
    const isNewSignup = sessionStorage.getItem('freshSignup') === 'true';
    
    // If this is a fresh signup, always direct to dashboard and clear the flag
    if (isNewSignup) {
      console.log('Fresh signup detected in navigation hook, redirecting to dashboard');
      sessionStorage.removeItem('freshSignup');
      
      if (location.pathname !== '/dashboard') {
        navigate('/dashboard', { replace: true });
        return; // Exit early - no other checks needed
      }
      return;
    }
    
    // Check if user has payment issues and redirect to subscription page if needed
    if (subscriptionStatus?.hasPaymentIssues && location.pathname !== '/dashboard/subscription') {
      console.log('Payment issues detected, user should update payment method');
      // Uncomment this if you want to force redirect to subscription page
      // navigate('/dashboard/subscription', { replace: true });
      // return;
    }
    
    // Mark initial check as complete
    initialCheckComplete.current = true;
    
    // Reset navigation flag when changing locations
    return () => {
      hasNavigatedRef.current = false;
    };
  }, [loading, isSubscriptionLoading, user, subscriptionStatus, navigate, location.pathname]);

  return;
};
